<template>
  <PageContainer v-if="fields" class="bg-pink">
    <template #header>
      <PageShingleWrapper class="bg-pink" next-class="bg-white">
        <DefaultGrid>
          <div
            class="col-span-full flex flex-col items-center pt-16 pb-[1em] text-[15vw]/[0.85em]"
          >
            <TransitionGroup
              :css="false"
              appear
              @enter="onEnter"
              @leave="onLeave"
            >
              <LockupCreativity v-if="headerCarouselIndex === 0" />
              <LockupFamilyTime v-else-if="headerCarouselIndex === 1" />
              <LockupThrills v-else-if="headerCarouselIndex === 2" />
            </TransitionGroup>
          </div>
        </DefaultGrid>
      </PageShingleWrapper>
    </template>
    <template #main>
      <section>
        <PageShingleWrapper class="bg-white" next-class="bg-white">
          <DefaultGrid
            class="space-y-20 py-20 md:space-y-24 md:py-24 xl:space-y-32 xl:py-32"
          >
            <div class="col-span-full">
              <div
                class="rich-text type-heading-4 mx-auto max-w-4xl text-center font-medium"
              >
                <PayloadRichText :content="fields.intro.body" />
              </div>
            </div>
            <div class="col-span-full space-y-5 md:space-y-10 xl:space-y-12">
              <div
                v-for="({ body, heading, largeText }, index) in fields.cards"
                :key="index"
                class="rounded-3xl px-7 py-12 md:px-14 md:py-24 xl:px-28 xl:py-32"
                :class="getCardBackgroundClass(index)"
              >
                <h2
                  class="type-heading-5 md:type-heading-3 mb-7 border-b border-black pb-7 font-medium md:mb-12 md:pb-12 xl:mb-16 xl:pb-16"
                  v-text="heading"
                />
                <div class="flex flex-col space-y-7">
                  <span
                    v-if="largeText"
                    class="text-[60px]/[1.1em] font-medium md:text-[100px]/[1.1em] xl:text-[125px]/[1.1em]"
                    v-text="largeText"
                  />
                  <div class="rich-text leading-[1.25em]">
                    <PayloadRichText :content="body" />
                  </div>
                </div>
              </div>
            </div>
          </DefaultGrid>
        </PageShingleWrapper>
      </section>
    </template>
  </PageContainer>
</template>

<script lang="ts" setup>
const initialData = await usePayloadPage('WhyWait')
const { data: doc } = useLivePreview({ initialData })

const fields = doc.value?.whyWaitTemplateFields

const { getCardBackgroundClass } = useGlobalsStore()

/**
 * Header Carousel
 */
const headerCarouselIndex = ref(-1)
const showContent = ref(false)
const timeoutID = ref<NodeJS.Timeout>()

watch(headerCarouselIndex, () => {
  timeoutID.value = setTimeout(() => {
    headerCarouselIndex.value = (headerCarouselIndex.value + 1) % 3
  }, 3500)
})

onMounted(() => {
  showContent.value = true
  headerCarouselIndex.value = 0
})

onBeforeUnmount(() => {
  clearTimeout(timeoutID.value)
})

const { $anime } = useNuxtApp()

const onEnter = (root: Element, complete: () => void) => {
  const anim = $anime.timeline({ complete })
  const largeText = root.querySelectorAll('.large-text span')
  const icons = root.querySelectorAll('.icons svg')

  anim
    .add(
      {
        targets: largeText,
        translateY: ['25%', 0],
        translateZ: 0,
        opacity: [0, 1],
        duration: 650,
        delay: $anime.stagger(150),
        easing: slideEasing,
      },
      500,
    )
    .add(
      {
        targets: shuffleArray([...icons]),
        scale: [0.5, 1],
        opacity: [0, 1],
        duration: 750,
        delay: $anime.stagger(250),
        easing: slideEasing,
      },
      1000,
    )
}

const onLeave = (root: Element, complete: () => void) => {
  const anim = $anime.timeline({ complete })
  const largeText = root.querySelectorAll('.large-text span')
  const icons = root.querySelectorAll('.icons svg')

  ;(root as HTMLElement).style.position = 'absolute'

  anim.add({
    targets: [largeText, icons],
    opacity: 0,
    duration: 500,
    easing: slideEasing,
  })
}
</script>
